/** A loaded Markdown document plus the context needed to resolve its links. */
export interface MarkdownDocument {
  content: string;
  /** Display name, e.g. a file name or the last URL segment. */
  name: string;
  /** Base used to resolve relative links and images. */
  baseUrl?: string;
}

/**
 * Where Markdown comes from.
 *
 * Keeping loading behind this interface means the viewer never needs to know
 * whether a document arrived from disk, a string, an HTTP response or a future
 * transport such as a content API.
 */
export interface MarkdownSource {
  readonly kind: string;
  load(): Promise<MarkdownDocument>;
}

/** Loads any source, normalising failures into a single error type. */
export const loadSource = async (source: MarkdownSource): Promise<MarkdownDocument> => {
  try {
    return await source.load();
  } catch (error) {
    throw new MarkdownSourceError(
      error instanceof Error ? error.message : 'The document could not be loaded.',
      source.kind,
      { cause: error },
    );
  }
};

export class MarkdownSourceError extends Error {
  constructor(message: string, readonly kind: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'MarkdownSourceError';
  }
}